import { useEffect, useState } from "react";
import { UnauthenticatedTemplate, AuthenticatedTemplate } from "@azure/msal-react";
import { ToasterContainer } from "baseui/toast";
import { DEV } from "./config";
import { BaseProvider, DarkTheme, LightTheme } from "baseui";
import { Client as Styletron } from "styletron-engine-atomic";
import { Provider as StyletronProvider } from "styletron-react";
import { useDarkMode } from "./hooks/useDarkMode";
import { routes } from "./pages/routes";
import { Routes, Route, BrowserRouter } from "react-router-dom";
import { LoginPage } from "./pages/LoginPage";
import { IntlProvider } from "react-intl";
import { SnackbarProvider } from "baseui/snackbar";
import { de } from "./lang/de";
import { EN } from "./lang/en";
import { ES } from "./lang/es";
import { useGetLanguage } from "./hooks/useGetLanguage";
import { AppInsightsErrorBoundary } from "@microsoft/applicationinsights-react-js";
import { reactPlugin } from "./appinsights";
import { ParagraphMedium } from "baseui/typography";

const engine = new Styletron();

function App() {
  const { isDarkMode } = useDarkMode();
  const theme = isDarkMode ? DarkTheme : LightTheme;
  const { language } = useGetLanguage();
  const [loc, setLoc] = useState<any>();

  useEffect(() => {
    // see https://en.wikipedia.org/wiki/List_of_ISO_639-1_codes
    if (language === "de" || language.startsWith("de-") || language.startsWith("deu")) {
      setLoc(de);
    } else if (language === "es" || language.startsWith("es-")) {
      setLoc(ES);
    } else {
      setLoc(EN);
    }
  }, [language]);

  useEffect(() => {
    if (DEV && !document.title.endsWith("DEV")) document.title += " - DEV";
  }, []);

  useEffect(() => {
    document.body.style.background = theme.colors.backgroundPrimary;
  }, [theme.colors.backgroundPrimary]);

  return (
    <StyletronProvider value={engine}>
      <BaseProvider theme={theme}>
        <IntlProvider locale={language} messages={loc}>
          <SnackbarProvider>
            <UnauthenticatedTemplate>
              <LoginPage />
            </UnauthenticatedTemplate>
            <AuthenticatedTemplate>
              <ToasterContainer>
                <AuthentificatedContainer />
              </ToasterContainer>
            </AuthenticatedTemplate>
          </SnackbarProvider>
        </IntlProvider>
      </BaseProvider>
    </StyletronProvider>
  );
}

export default App;

function AuthentificatedContainer() {
  return (
    <AppInsightsErrorBoundary
      onError={() => <ParagraphMedium>Sorry, Something went wrong. Try to refresh the page</ParagraphMedium>}
      appInsights={reactPlugin}>
      <BrowserRouter>
        <Routes>
          {routes.map((elm) => (
            <Route key={elm.path} {...elm} />
          ))}
        </Routes>
      </BrowserRouter>
    </AppInsightsErrorBoundary>
  );
}
